import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { CHART_COLORS, getAgChartTheme } from '../../analytics/charts/chartTheme';
import { useTheme } from '../../../stores/ThemeContext';

const EMOTION_BAR_COLORS = {
  calm: '#22c55e',
  confident: '#3b82f6',
  neutral: '#a1a1aa',
  anxious: '#f59e0b',
  frustrated: '#ef4444',
  excited: '#8b5cf6',
};

const EmotionOutcomeChart = ({ data = [], height = 300 }) => {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) setDimensions({ width, height });
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [height]);

  const chartData = useMemo(() => {
    return data
      .filter(d => d.trade_count > 0)
      .map(d => ({
        ...d,
        emotionLabel: d.emotion.charAt(0).toUpperCase() + d.emotion.slice(1),
        fill: EMOTION_BAR_COLORS[d.emotion] || CHART_COLORS.neutral,
      }));
  }, [data]);

  const options = useMemo(() => ({
    theme: getAgChartTheme(isDark),
    width: dimensions.width || undefined,
    height: dimensions.height,
    data: chartData,
    series: [
      {
        type: 'bar',
        xKey: 'emotionLabel',
        yKey: 'win_rate',
        yName: 'Win Rate %',
        formatter: ({ datum }) => ({
          fill: datum.fill,
        }),
        tooltip: {
          renderer: ({ datum }) => ({
            title: datum.emotionLabel,
            content: `Win Rate: ${datum.win_rate}%\nTrades: ${datum.trade_count}`,
          }),
        },
        label: {
          enabled: true,
          formatter: ({ value }) => `${value}%`,
          color: isDark ? '#fafafa' : '#111827',
          fontSize: 10,
        },
      },
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        label: {
          color: isDark ? '#a1a1aa' : '#6b7280',
          fontSize: 11,
        },
      },
      {
        type: 'number',
        position: 'left',
        min: 0,
        max: 100,
        label: {
          color: isDark ? '#a1a1aa' : '#6b7280',
          formatter: ({ value }) => `${value}%`,
        },
      },
    ],
  }), [chartData, dimensions, isDark]);

  if (!chartData.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-sm font-semibold text-foreground mb-4">Emotion vs Outcome</h3>
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
          No emotion data yet. Journal trades to see which mindset produces the best results.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <h3 className="text-sm font-semibold text-foreground mb-4">Emotion vs Outcome</h3>
      <div ref={containerRef} className="w-full">
        {dimensions.width > 0 && <AgCharts options={options} />}
      </div>
    </div>
  );
};

export default EmotionOutcomeChart;
